import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import {
	GroupRequestType,
	GroupResourceRequest,
	GroupResourceType,
} from "@/model/Group/Group";
import { GroupMembershipType } from "@/model/User/Employee";

import { GroupQueryKeys } from "@/service/GroupQueries";

export const useMutateCreateGroup = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: createGroup } = useMutation({
		mutationFn: (request: GroupRequestType) => {
			return SATOSPortal.CreateGroup(request);
		},

		onSuccess: (data) => {
			queryClient.invalidateQueries({queryKey: [GroupQueryKeys.groups, data.Id]});
			queryClient.invalidateQueries({queryKey: [GroupQueryKeys.groups]});
		},
	});

	return createGroup;
};

export const useMutateUpdateGroup = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: updateGroup } = useMutation({
		mutationFn: ({
			request,
			groupId,
		}: {
			request: GroupRequestType;
			groupId: number;
		}) => {
			return SATOSPortal.UpdateGroup(request, groupId);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({queryKey: [GroupQueryKeys.groups, data.Id]});
			queryClient.invalidateQueries({queryKey: [GroupQueryKeys.groups]});
		},
	});

	return updateGroup;
};

export const useMutateDeleteGroup = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: deleteGroup } = useMutation({
		mutationFn: (groupId: number) => {
			return SATOSPortal.DeleteGroup(groupId);
		},
		onSuccess: (_, groupId) => {
			queryClient.invalidateQueries({queryKey: [GroupQueryKeys.groups, groupId]});
			queryClient.invalidateQueries({queryKey: [GroupQueryKeys.groups]});
		},
	});

	return deleteGroup;
};

export const useMutateUpdateGroupMembership = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: updateGroupMembership } = useMutation({
		mutationFn: ({
			request,
			userId,
			groupId,
		}: {
			request: GroupMembershipType;
			userId: number;
			groupId: number;
		}) => {
			return SATOSPortal.UpdateGroupMembership(request, userId, groupId);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({queryKey: [
				GroupQueryKeys.groups,
				data.GroupId,
				GroupQueryKeys.members,
			]});
			queryClient.invalidateQueries({queryKey: [GroupQueryKeys.groups, data.GroupId]});
			queryClient.invalidateQueries({queryKey: [GroupQueryKeys.groups]});
		},
	});

	return updateGroupMembership;
};

export const useMutateUpdateGroupResource = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: updateGroupResource } = useMutation({
		mutationFn: ({
			request,
			groupId,
			resourceType,
		}: {
			request: GroupResourceRequest;
			groupId: number;
			resourceType: GroupResourceType;
		}) => {
			return SATOSPortal.updateGroupResource(request, groupId, resourceType);
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({queryKey: [
				GroupQueryKeys.groups,
				variables.groupId,
				GroupQueryKeys.resources,
			]});
		},
	});

	return updateGroupResource;
};

export const useMutateUpdateTargetTime = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: updateTargetTime } = useMutation({
		mutationFn: ({
			request,
			ChecklistId,
		}: {
			request;
			ChecklistId: number;
		}) => {
			return SATOSPortal.updateTargetTime(request, ChecklistId);
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({queryKey: [
				GroupQueryKeys.groups,
				GroupQueryKeys.resources,
			]});
		},
	});

	return updateTargetTime;
};
